import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { roomWalker } from '../state/roomBody';
import { useFrame, useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { useGymFuel, drinkFuel, clearFuel } from '../state/gymFuel';
import { seatMotion } from '../state/seatMotion';

// GymFloor — the GangstaFuel Gym's equipment and its fuel bar (2026-10-31).
//
// Khari: "There are weights in the gym, but to keep things simple, they can use
// the treadmill (run in place), and a bench press (lay and view upward). …
// Like the coffee shop, a drink is available. It is 'GangstaFuel' Energy Drink.
// When the user drinks it, their movement is sped up for 1 minute and the screen
// is brighter."
//
// THE EQUIPMENT IS USED THE WAY A CHAIR IS. The room model (scripts/make_gym.mjs)
// names every machine and leaves an empty `use_<unit>` node where the user's
// body goes. Walk up to one and it is offered (RoomMarkers' halo and button,
// like the café's chairs); the button sits you in a pose this file computes —
//   · treadmill: standing on the belt, facing the console, the belt running
//     under you and your eye bouncing with the stride (state/seatMotion, added
//     by SeatCamera to its hold);
//   · bench press: lying on the bench, looking straight up at the bar, which
//     comes down toward you and goes back up, rep after rep;
// — and the seat system does everything else: the glide in and out, Done (and
// its two-minute clock: the gym's machines are SHARED SEATS, so nobody takes
// the treadmill you are on), the walk switched off, standing up where you were.
//
// THE CAN. The instructor's hand_fuel puts one on the bar (state/gymFuel, at
// rooms.js `fuel.pickup`). Tap it (or "Drink it" in the dock) and it is gone and
// the minute starts (EnergyBoost; the walk code reads the speed).

const NEAR = 1.0;          // world units from a machine's use spot to be offered it
const LEVEL = 0.6;         // …and on its floor
const RUN_EYE = 0.75;      // standing eye above the belt
const LIE_EYE = 0.1;       // lying eye above the bench pad
const LIE_PITCH = 1.42;    // looking (almost) straight up
const STRIDE_HZ = 2.7;     // steps a second, an easy run
const BELT_SPEED = 4;      // slat repeats a second (one per 0.5m: an easy run); +offset.y runs toward the back (make_gym)
const REP_S = 3.4;         // one rep: down, pause, up, hold
const REP_DROP = 0.115;    // how far the bar comes down: racked → chest (~0.7m), plates clear of the floor (make_gym)
const LIME = '#74f84b';

/** The machines in the model: { name, kind: 'treadmill'|'bench', spot, belt, bar }. */
function findGear(model) {
  const out = [];
  if (!model) return out;
  model.traverse((o) => {
    if (!o.userData?.useSpot) return;
    const name = String(o.name || '').replace(/^use_/, '');
    const kind = o.userData.use === 'bench' ? 'bench' : o.userData.use === 'treadmill' ? 'treadmill' : null;
    if (!name || !kind) return;
    out.push({ name, kind, spot: o, belt: model.getObjectByName(`${name}_belt`) || null, bar: model.getObjectByName(`${name}_bar`) || null });
  });
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

const v = new THREE.Vector3();
const dir = new THREE.Vector3();

/** Where using a machine puts you, in world space (the seat system's pose shape). */
function poseFor(g) {
  g.spot.updateWorldMatrix(true, false);
  g.spot.getWorldPosition(v);
  const f = typeof g.spot.userData.facing === 'number' ? g.spot.userData.facing : 0;
  dir.set(Math.sin(f), 0, Math.cos(f)).transformDirection(g.spot.matrixWorld).setY(0).normalize();
  const yaw = Math.atan2(dir.x, dir.z);
  return g.kind === 'bench'
    ? { pos: [v.x, v.y + LIE_EYE, v.z], yaw, pitch: LIE_PITCH, label: 'Bench press' }
    : { pos: [v.x, v.y + RUN_EYE, v.z], yaw, pitch: -0.04, label: 'Run' };
}

function Can({ at, k }) {
  const [hot, setHot] = useState(false);
  const g = useRef();
  useFrame((st) => { if (g.current) g.current.position.y = at[1] * k + 0.004 + Math.sin(st.clock.elapsedTime * 2.2) * 0.003; });
  const tap = (e) => { e.stopPropagation(); drinkFuel(); document.body.style.cursor = ''; };
  return (
    <group ref={g} position={[at[0] * k, at[1] * k, at[2] * k]}>
      <group
        onClick={tap}
        onPointerOver={(e) => { e.stopPropagation(); setHot(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHot(false); document.body.style.cursor = ''; }}
      >
        {/* A 473ml can, ~16cm tall: black, a lime band, a silver top. */}
        <mesh position={[0, 0.036, 0]} castShadow>
          <cylinderGeometry args={[0.0145, 0.0145, 0.072, 20]} />
          <meshStandardMaterial color="#0d0f0c" roughness={0.35} metalness={0.6} />
        </mesh>
        <mesh position={[0, 0.04, 0]}>
          <cylinderGeometry args={[0.0148, 0.0148, 0.022, 20, 1, true]} />
          <meshStandardMaterial color={LIME} emissive={LIME} emissiveIntensity={hot ? 1.4 : 0.8} roughness={0.4} metalness={0.2} />
        </mesh>
        <mesh position={[0, 0.0735, 0]}>
          <cylinderGeometry args={[0.012, 0.0145, 0.003, 20]} />
          <meshStandardMaterial color="#c9ccc6" roughness={0.3} metalness={0.9} />
        </mesh>
        {/* A bigger, invisible thing to tap than a 3cm can. */}
        <mesh position={[0, 0.04, 0]} visible={false}>
          <cylinderGeometry args={[0.05, 0.05, 0.12, 8]} />
          <meshBasicMaterial />
        </mesh>
      </group>
      <Text position={[0, 0.115, 0]} fontSize={0.03} color={LIME} anchorX="center" anchorY="bottom" outlineWidth={0.003} outlineColor="#000">
        {hot ? 'Drink it' : '⚡'}
      </Text>
    </group>
  );
}

export default function GymFloor({ roomId, model, k = 1 }) {
  const room = ROOMS[roomId];
  const camera = useThree((s) => s.camera);
  const gear = useMemo(() => findGear(model), [model]);
  const can = useGymFuel((s) => (s.can?.zone === roomId ? s.can : null));
  const marked = useRef('');
  const acc = useRef(0);
  const using = useRef(null);   // { g, t0, bar0, down, beltMat, beltOrig }

  useEffect(() => {
    if (gear.length) console.info(`[gym] ${roomId}: ${gear.map((g) => `${g.name} (${g.kind})`).join(', ')}`);
    else console.warn(`[gym] ${roomId}: no use_* nodes in the model — equipment cannot be used`);
  }, [gear, roomId]);

  const stopUsing = () => {
    const u = using.current;
    if (!u) return;
    if (u.g.bar && u.bar0) u.g.bar.position.copy(u.bar0);
    if (u.g.belt && u.beltOrig) { u.g.belt.material.map?.dispose?.(); u.g.belt.material.dispose?.(); u.g.belt.material = u.beltOrig; }
    seatMotion.fn = null;
    using.current = null;
  };

  // Out of the room: the offers, the stride and the can go with you.
  useEffect(() => () => {
    stopUsing();
    if (marked.current) useWorldStore.getState().setMarks([]);
    clearFuel(roomId);
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((st, dt) => {
    const ws = useWorldStore.getState();
    const seated = ws.seated;
    const on = seated?.phase === 'seated' ? gear.find((g) => g.name === seated.target) : null;

    // ── IN USE ─────────────────────────────────────────────────────────────
    if (on && using.current?.g !== on) {
      stopUsing();
      const u = { g: on, t0: st.clock.elapsedTime };
      if (on.kind === 'treadmill') {
        seatMotion.fn = (t, target) => (target === on.name
          ? [0, Math.abs(Math.sin(Math.PI * STRIDE_HZ * t)) * 0.016, Math.sin(Math.PI * STRIDE_HZ * t * 0.5) * 0.004]
          : null);
        // Its own copy of the belt, so only YOUR treadmill runs.
        if (on.belt?.material?.map) {
          u.beltOrig = on.belt.material;
          const m = on.belt.material.clone();
          m.map = on.belt.material.map.clone();
          m.map.wrapS = m.map.wrapT = THREE.RepeatWrapping;
          m.map.needsUpdate = true;
          on.belt.material = m;
        }
      } else if (on.bar) {
        // Down toward the chest = down, and a little toward the feet, in the bar's own parent space.
        u.bar0 = on.bar.position.clone();
        const p = on.bar.parent;
        on.bar.getWorldPosition(v);
        const pose = seated.seat;
        const feet = new THREE.Vector3(Math.sin(pose.yaw), 0, Math.cos(pose.yaw));
        const a = p.worldToLocal(v.clone());
        const b = p.worldToLocal(v.clone().add(new THREE.Vector3(0, -REP_DROP, 0)).addScaledVector(feet, REP_DROP * 0.5));
        u.down = b.sub(a);
      }
      using.current = u;
    } else if (!on && using.current) {
      stopUsing();
    }
    const u = using.current;
    if (u) {
      if (u.g.kind === 'treadmill' && u.g.belt?.material?.map && u.beltOrig) u.g.belt.material.map.offset.y += BELT_SPEED * dt;
      if (u.g.kind === 'bench' && u.bar0 && u.down) {
        const t = ((st.clock.elapsedTime - u.t0) % REP_S) / REP_S;
        // hold at the top, lower, pause at the chest, press
        const d = t < 0.2 ? 0 : t < 0.55 ? (t - 0.2) / 0.35 : t < 0.62 ? 1 : t < 0.9 ? 1 - (t - 0.62) / 0.28 : 0;
        const e = d * d * (3 - 2 * d);
        u.g.bar.position.copy(u.bar0).addScaledVector(u.down, e);
      }
    }

    // ── OFFERS: the machines near you ──────────────────────────────────────
    acc.current += dt;
    if (acc.current < 0.25) return;
    acc.current = 0;
    const now = Date.now();
    const holds = room?.sharedSeats && ws.roomSeats?.zone === roomId ? ws.roomSeats.seats : null;
    const taken = (name) => { const h = holds?.[name]; return !!h && !h.mine && h.until > now; };
    const p = roomWalker(camera);   // the body in third person (state/roomBody)
    const near = [];
    for (const g of gear) {
      g.spot.getWorldPosition(v);
      const d = Math.hypot(v.x - p.x, v.z - p.z);
      if (d < NEAR && Math.abs(v.y - (p.y - RUN_EYE)) < LEVEL && !taken(g.name)) near.push({ g, d });
    }
    near.sort((x, y) => x.d - y.d);
    const list = near.slice(0, 2).map((n) => n.g);
    const mine = seated && seated.phase !== 'standing' ? gear.find((g) => g.name === seated.target) : null;
    if (mine && !list.includes(mine)) list.unshift(mine);   // your own machine keeps its "Stand up"
    const key = list.map((g) => g.name).join('|');
    if (key !== marked.current) {
      marked.current = key;
      ws.setMarks(list.map((g) => ({ target: g.name, pose: poseFor(g) })));
    }
  });

  const pickup = room?.fuel?.pickup;
  return can && pickup ? <Can at={pickup} k={k} /> : null;
}
